import { MsalAuthenticationTemplate } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest, protectedResources } from "../authConfig";
import LoadingSpinner from "../components/LoadingSpinner";
import ShowToast from '../components/ShowToast.jsx';
import React, { useEffect, useState } from 'react';
import useFetchWithMsal from '../hooks/useFetchWithMsal';
import { Modal, Button, Form } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { DateTime } from "luxon";
import '@fortawesome/fontawesome-free/css/all.min.css';

export const HolidaysGrid = (props) => {
    const STORAGE_KEY = "HolidaysGrid_filters";

    const authRequest = { ...loginRequest, };

    const { isLoading, error, execute } = useFetchWithMsal({
        scopes: protectedResources.api.scopes
    });

    const loadStoredFilters = () => {
        const storedFilters = localStorage.getItem(STORAGE_KEY);
        return storedFilters ? JSON.parse(storedFilters) : { fecha: "", descripcion: "" };
    };

    const [data, setData] = useState([]);
    const [filters, setFilters] = useState(loadStoredFilters);
    const [showModal, setShowModal] = useState(false);
    const [selectedHoliday, setSelectedHoliday] = useState(null);
    const [editForm, setEditForm] = useState({ day: "", description: "" });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newHoliday, setNewHoliday] = useState({ day: "", description: "" });
    const [formErrors, setFormErrors] = useState({});
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [holidayToDelete, setHolidayToDelete] = useState(null);

    useEffect(() => {
        execute("GET", protectedResources.api.holidays).then((response) => {
            if (response?.data) {
                setData(response.data);
            }
        });

    }, [execute]);

    const filteredData = data.filter(row => {
        const fechaFormatted = DateTime.fromISO(row.day, { setZone: 'utc' }).toFormat('yyyy-MM-dd');
        const descripcion = row.description ? row.description.toLowerCase() : "";

        return (
            (!filters.fecha || fechaFormatted.includes(filters.fecha)) &&
            (!filters.descripcion || descripcion.includes(filters.descripcion.toLowerCase()))
        );
    });

    const handleFilterChange = (field, value) => {
        setFilters(prev => {
            const newFilters = { ...prev, [field]: value };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(newFilters));
            return newFilters;
        });
    };

    const handleCreateChange = (field, value) => {
        setNewHoliday(prev => ({ ...prev, [field]: value }));
    };

    const validateHolidayForm = (formData) => {
        const newErrors = {};
        if (!formData.day) newErrors.day = 'La fecha es obligatoria';
        if (!formData.description) newErrors.description = 'La descripción es obligatoria';
        if (data.some(d => d.day === formData.day && d.day !== selectedHoliday?.day)) {
            newErrors.day = "Ya existe un día feriado en esa fecha.";
        }
        return newErrors;
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        const validationErrors = validateHolidayForm(newHoliday);
        if (Object.keys(validationErrors).length > 0) {
            setFormErrors(validationErrors);
            return;
        }

        try {
            let creado = await execute("POST", protectedResources.api.holidays, newHoliday);
            let res = await execute("GET", protectedResources.api.holidays);
            setData(res?.data || []);
            setShowCreateModal(false);
            setNewHoliday({ day: "", description: "" });
            handleCloseModal();
            showServiceResult(creado, 201, "Día feriado creado correctamente.", "Error al crear el día feriado.");
        } catch (error) {
            ShowToast("error", error?.response?.data?.message || "Ocurrió un error al crear el día feriado.");
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        const validationErrors = validateHolidayForm(editForm);
        if (Object.keys(validationErrors).length > 0) {
            setFormErrors(validationErrors);
            return;
        }

        try {
            let actualizado = await execute("PUT", `${protectedResources.api.holidays}/${selectedHoliday.day}`, {
                day: editForm.day,
                description: editForm.description
            });
            const res = await execute("GET", protectedResources.api.holidays);
            setData(res?.data || []);
            handleCloseModal();
            showServiceResult(actualizado, 200, "Día feriado actualizado correctamente.", "Error al actualizar el día feriado.");
        } catch (error) {
            ShowToast("error", error?.response?.data?.message || "Ocurrió un error al actualizar el día feriado.");
        }
    };

    const handleShowModal = (holiday) => {
        setSelectedHoliday(holiday);
        setEditForm({
            day: DateTime.fromISO(holiday.day, { setZone: 'utc' }).toFormat('yyyy-MM-dd'),
            description: holiday.description || ""
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedHoliday(null);
        setFormErrors({});
    };

    const handleEditChange = (field, value) => {
        setEditForm(prev => ({ ...prev, [field]: value }));
    };

    const handleDeleteClick = (holiday) => {
        setHolidayToDelete(holiday);
        setShowDeleteModal(true);
    };

    const columns = [
        {
            // name: "Fecha",
            selector: row => DateTime.fromISO(row.day, { setZone: 'utc' }).toFormat('yyyy-MM-dd'),
            sortable: true,
            grow: 1,
            width: "30%",
            cell: row => DateTime.fromISO(row.day, { setZone: 'utc' }).toFormat('yyyy-MM-dd'),
            filterComponent: (
                <input
                    type="text"
                    placeholder="Fecha"
                    className="form-control"
                    value={filters.fecha}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => handleFilterChange("fecha", e.target.value)}
                />
            )
        },
        {
            // name: "Descripción",
            selector: row => row.description,
            sortable: true,
            grow: 1,
            width: "45%",
            cell: row => row.description,
            filterComponent: (
                <input
                    type="text"
                    placeholder="Descripción"
                    className="form-control"
                    value={filters.descripcion}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => handleFilterChange("descripcion", e.target.value)}
                />
            )
        },
        {
            name: "Acciones",
            cell: row => (
                <div className="d-flex gap-2">
                    <Button variant="warning" size="sm" onClick={() => handleShowModal(row)}>
                    <i className="fa fa-pencil-alt" />{/* Editar */}
                    </Button>
                    <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteClick(row)}
                    >
                        <i className="fa fa-trash" />{/* Eliminar */}
                    </Button>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "25%"
        }

    ];

    const showServiceResult = (res, expectedStatus, successMsg, errorMsg) => {
        if (res?.status == expectedStatus) {
            ShowToast("success", res?.message || successMsg);
        } else {
            ShowToast("error", res?.message || errorMsg);
        }
    };

    const renderForm = (
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={authRequest}>
            <div className="container">
                <div className="d-flex justify-content-end pe-3 pt-3">
                    <Button className="mb-3" onClick={() => setShowCreateModal(true)}>
                        <i className="fa fa-plus me-2" /> Agregar día feriado
                    </Button>
                </div>

                <DataTable
                    columns={columns.map(col => ({
                        ...col,
                        name: (
                            <div style={{ textAlign: 'center', width: '100%' }}>
                                {col.name}
                                {col.filterComponent && <div>{col.filterComponent}</div>}
                            </div>
                        )
                    }))}
                    data={filteredData}
                    pagination
                    highlightOnHover
                    responsive
                    fixedHeader
                    noDataComponent="No hay datos disponibles"
                    persistTableHead
                />
                {/* Modal para agregar holidays */}
                <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Nuevo día feriado</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group>
                                <Form.Label>Fecha</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={newHoliday.day}
                                    onChange={(e) => handleCreateChange("day", e.target.value)}
                                    isInvalid={!!formErrors.day}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.day}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mt-2">
                                <Form.Label>Descripción</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={newHoliday.description}
                                    onChange={(e) => handleCreateChange("description", e.target.value)}
                                    isInvalid={!!formErrors.description}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.description}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Cancelar</Button>
                        <Button variant="success" onClick={handleCreate}>Crear</Button>
                    </Modal.Footer>
                </Modal>
                {/* Modal para agregar holidays */}

                {/* Modal para editar holidays */}
                <Modal show={showModal} onHide={handleCloseModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>Editar día feriado</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group>
                                <Form.Label>Fecha</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={editForm.day}
                                    onChange={(e) => handleEditChange("day", e.target.value)}
                                    isInvalid={!!formErrors.day}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.day}
                                </Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mt-2">
                                <Form.Label>Descripción</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={editForm.description}
                                    onChange={(e) => handleEditChange("description", e.target.value)}
                                    isInvalid={!!formErrors.description}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.description}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>Cancelar</Button>
                        <Button variant="primary" onClick={handleUpdate}>Guardar</Button>
                    </Modal.Footer>
                </Modal>
                {/* Modal para editar holidays */}

                {/* Modal para eliminar holidays */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirmar eliminación</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        ¿Estás seguro de que deseas eliminar el día feriado del{" "}
                        <strong>{holidayToDelete?.day}</strong>?
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                            Cancelar
                        </Button>
                        <Button variant="danger" onClick={async () => {
                            try {
                                let eliminado = await execute("DELETE", `${protectedResources.api.holidays}/${holidayToDelete.day}`);
                                const res = await execute("GET", protectedResources.api.holidays);
                                setData(res?.data || []);
                                setShowDeleteModal(false);
                                setHolidayToDelete(null);
                                showServiceResult(eliminado, 200, "Día feriado eliminado correctamente.", "Error al eliminar el día feriado.");
                            } catch (err) {
                                ShowToast("error", err?.response?.data?.message || "Ocurrió un error al eliminar el día feriado.");
                            }
                        }}>
                            Aceptar
                        </Button>
                    </Modal.Footer>
                </Modal>
                {/* Modal para eliminar holidays */}

            </div>
        </MsalAuthenticationTemplate>
    );

    return (
        <div className="container"> {isLoading ? <LoadingSpinner /> : error ? <div className="alert alert-danger mt-3">Error al cargar los días feriados.</div> : renderForm} </div>
    );
}